/**
 * screens/integrations/integrations.tsx
 * Integrations & sourcing — a single, premium "Coming soon" experience:
 * ambient drifting orbs, a haloed hero, rotating hopeful hooks, benefit
 * pillars and an early-access band. Data is preserved: job portals
 * (/api/hire/v2/portals) + SK Bridge are surfaced as the "arriving" preview.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, ScrollView, Animated, Easing, Platform } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';

import { useTheme } from '../../context/themeContext';
import { useBreakpoints } from '../../theme/responsive';
import { listPortals, type Portal } from '../../services/hire/integrationV2';
import { Icon } from '../../components/ui';

// --- Preserved data (nothing removed) ------------------------------------------
const SERVICES = [
  { name: 'SK Drive', desc: 'Resume storage + AV scan', tone: 'success' as const, status: 'Healthy' },
  { name: 'SK Meter', desc: 'Prepaid AI credits (block-at-cap)', tone: 'success' as const, status: 'Healthy' },
  { name: 'SK CRM', desc: 'Client account references (agency)', tone: 'success' as const, status: 'Healthy' },
  { name: 'SK Bridge', desc: 'Inbound webhooks · HMAC · 5-min replay', tone: 'success' as const, status: 'Healthy' },
  { name: 'SK Notifications', desc: 'Candidate + recruiter alerts', tone: 'success' as const, status: 'Healthy' },
  { name: 'SK Billing', desc: 'Placement billing', tone: 'success' as const, status: 'Healthy' },
  { name: 'Key envelope', desc: 'PII crypto-shred (SK Face/Pass)', tone: 'warn' as const, status: 'Pending' },
];

const OUTBOUND = [
  { event: 'candidate.ingested', to: 'backbone → subscribers' },
  { event: 'match_run.completed', to: 'backbone → subscribers' },
  { event: 'action.recorded', to: 'backbone → subscribers' },
  { event: 'candidate.erased', to: 'backbone → subscribers' },
];

/**
 * Rotating hooks. Bulk import used to be the hero here — it has since shipped and
 * lives on Jobs and Candidates, so this screen is now about what it cannot do:
 * reaching candidates who are not yet in a file on your machine.
 */
const HOPEFUL_MESSAGES = [
  'Source straight from LinkedIn Recruiter — no copy, no paste.',
  'Naukri applicants land in your pool the moment they apply.',
  'One pipeline, every board — matched against your open roles.',
  'Applicants arrive scored, not just collected.',
  'Post once. Sync everywhere. Follow up in one place.',
];

const NATIVE = Platform.OS !== 'web';

export default function IntegrationsScreen() {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const { isMobile } = useBreakpoints();

  const [portals, setPortals] = useState<Portal[] | null>(null);

  const load = useCallback(() => {
    let active = true;
    listPortals().then((p) => { if (active) setPortals(p); }).catch(() => { if (active) setPortals([]); });
    return () => { active = false; };
  }, []);
  useFocusEffect(load);

  // --- Animation drivers ---------------------------------------------------
  const enter = useRef(new Animated.Value(0)).current;    // card entrance
  const pulse = useRef(new Animated.Value(0)).current;     // glow ring pulse
  const float = useRef(new Animated.Value(0)).current;     // icon float
  const shimmer = useRef(new Animated.Value(0)).current;   // sweeps
  const drift = useRef(new Animated.Value(0)).current;     // ambient orbs
  const beat = useRef(new Animated.Value(0)).current;      // early-access dot
  const msg = useRef(new Animated.Value(1)).current;       // message crossfade
  const [msgIndex, setMsgIndex] = useState(0);

  useEffect(() => {
    Animated.spring(enter, { toValue: 1, tension: 42, friction: 9, useNativeDriver: NATIVE }).start();

    const loop = (v: Animated.Value, dur: number) =>
      Animated.loop(
        Animated.sequence([
          Animated.timing(v, { toValue: 1, duration: dur, easing: Easing.inOut(Easing.ease), useNativeDriver: NATIVE }),
          Animated.timing(v, { toValue: 0, duration: dur, easing: Easing.inOut(Easing.ease), useNativeDriver: NATIVE }),
        ]),
      );

    const anims = [
      loop(pulse, 1900),
      loop(float, 2600),
      loop(drift, 9000),
      loop(beat, 1100),
      Animated.loop(Animated.timing(shimmer, { toValue: 1, duration: 2400, easing: Easing.inOut(Easing.ease), useNativeDriver: NATIVE })),
    ];
    anims.forEach((a) => a.start());
    return () => anims.forEach((a) => a.stop());
  }, [enter, pulse, float, shimmer, drift, beat]);

  // Rotate the hopeful hook with a gentle crossfade.
  useEffect(() => {
    const id = setInterval(() => {
      Animated.timing(msg, { toValue: 0, duration: 420, easing: Easing.in(Easing.ease), useNativeDriver: NATIVE }).start(() => {
        setMsgIndex((i) => (i + 1) % HOPEFUL_MESSAGES.length);
        Animated.timing(msg, { toValue: 1, duration: 480, easing: Easing.out(Easing.ease), useNativeDriver: NATIVE }).start();
      });
    }, 3400);
    return () => clearInterval(id);
  }, [msg]);

  // "Arriving" preview → job portals + SK Bridge only (nothing else).
  const arriving = [
    ...(portals ?? []).map((p) => ({ key: `p:${p.slug}`, label: p.displayName, hint: `${p.sourcedCount} sourced` })),
    ...SERVICES.filter((s) => s.name === 'SK Bridge').map((s) => ({ key: `s:${s.name}`, label: s.name, hint: s.desc })),
  ];

  const glowScale = pulse.interpolate({ inputRange: [0, 1], outputRange: [1, 1.35] });
  const glowOpacity = pulse.interpolate({ inputRange: [0, 1], outputRange: [0.28, 0] });
  const ring2Scale = pulse.interpolate({ inputRange: [0, 1], outputRange: [1, 1.75] });
  const ring2Opacity = pulse.interpolate({ inputRange: [0, 1], outputRange: [0.18, 0] });
  const iconFloat = float.interpolate({ inputRange: [0, 1], outputRange: [0, -8] });
  const sweepX = shimmer.interpolate({ inputRange: [0, 1], outputRange: [-140, 140] });
  const cardTranslate = enter.interpolate({ inputRange: [0, 1], outputRange: [28, 0] });
  const cardScale = enter.interpolate({ inputRange: [0, 1], outputRange: [0.96, 1] });
  const msgTranslate = msg.interpolate({ inputRange: [0, 1], outputRange: [6, 0] });
  const beatScale = beat.interpolate({ inputRange: [0, 1], outputRange: [1, 1.9] });
  const beatOpacity = beat.interpolate({ inputRange: [0, 1], outputRange: [0.55, 0] });
  const orbA = drift.interpolate({ inputRange: [0, 1], outputRange: [-24, 24] });
  const orbB = drift.interpolate({ inputRange: [0, 1], outputRange: [20, -20] });

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: isMobile ? 18 : 32, paddingBottom: 60, flexGrow: 1, justifyContent: 'center' }}>
      {/* Ambient drifting orbs (premium backdrop) */}
      <Animated.View
        pointerEvents="none"
        style={{
          position: 'absolute', top: '8%', left: '10%', width: 260, height: 260, borderRadius: radii.full,
          opacity: 0.5, transform: [{ translateX: orbA }, { translateY: orbB }],
          ...(Platform.OS === 'web'
            ? ({ backgroundImage: `radial-gradient(circle, ${colors.accentSoft} 0%, transparent 70%)`, filter: 'blur(8px)' } as any)
            : { backgroundColor: colors.accentSoft }),
        }}
      />
      <Animated.View
        pointerEvents="none"
        style={{
          position: 'absolute', bottom: '6%', right: '8%', width: 320, height: 320, borderRadius: radii.full,
          opacity: 0.45, transform: [{ translateX: orbB }, { translateY: orbA }],
          ...(Platform.OS === 'web'
            ? ({ backgroundImage: `radial-gradient(circle, ${colors.accentSoft} 0%, transparent 70%)`, filter: 'blur(8px)' } as any)
            : { backgroundColor: colors.accentSoft }),
        }}
      />

      <Animated.View
        style={{
          width: '100%',
          maxWidth: isMobile ? 480 : 760,
          alignSelf: 'center',
          opacity: enter,
          transform: [{ translateY: cardTranslate }, { scale: cardScale }],
        }}
      >
        <View
          style={{
            borderRadius: radii.xl + 6,
            borderWidth: 1,
            borderColor: colors.line,
            backgroundColor: colors.paper,
            paddingVertical: isMobile ? 18 : 24,
            paddingHorizontal: isMobile ? 20 : 36,
            alignItems: 'center',
            overflow: 'hidden',
            ...(Platform.OS === 'web'
              ? ({
                  backgroundImage: `radial-gradient(120% 90% at 50% -10%, ${colors.accentSoft} 0%, ${colors.paper} 55%)`,
                  boxShadow: `0 30px 70px -32px ${colors.accent}66, 0 2px 8px -4px ${colors.accent}22`,
                } as any)
              : {}),
          }}
        >
          {/* Eyebrow */}
          <Text style={{ fontFamily: font.mono, fontSize: 10, letterSpacing: 3, fontWeight: fontWeight.semibold, color: colors.ink4, marginBottom: 14 }}>
            SK HIRE · INTEGRATIONS
          </Text>

          {/* Halo icon with pulsing rings + gentle float */}
          <View style={{ height: 60, alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
            <Animated.View
              style={{
                position: 'absolute', width: 64, height: 64, borderRadius: radii.full,
                backgroundColor: colors.accent, opacity: glowOpacity, transform: [{ scale: glowScale }],
              }}
            />
            <Animated.View
              style={{
                position: 'absolute', width: 64, height: 64, borderRadius: radii.full,
                borderWidth: 1, borderColor: colors.accentLine, opacity: ring2Opacity, transform: [{ scale: ring2Scale }],
              }}
            />
            <Animated.View
              style={{
                width: 52, height: 52, borderRadius: radii.full,
                backgroundColor: colors.accent, alignItems: 'center', justifyContent: 'center',
                transform: [{ translateY: iconFloat }],
                ...(Platform.OS === 'web'
                  ? ({ backgroundImage: `linear-gradient(160deg, ${colors.accent}, ${colors.accentInk})`, boxShadow: `0 14px 30px -12px ${colors.accent}` } as any)
                  : {}),
              }}
            >
              <Icon name="link" size={22} color={colors.onAccent} />
            </Animated.View>
          </View>

          {/* Coming-soon pill with a pulsing dot + light sweep */}
          <View
            style={{
              flexDirection: 'row', alignItems: 'center', gap: 8,
              paddingHorizontal: 14, paddingVertical: 6, borderRadius: radii.full,
              borderWidth: 1, borderColor: colors.accentLine, backgroundColor: colors.accentSoft,
              overflow: 'hidden', marginBottom: 12,
            }}
          >
            <Animated.View
              pointerEvents="none"
              style={{
                position: 'absolute', top: 0, bottom: 0, width: 60, opacity: 0.5,
                transform: [{ translateX: sweepX }, { rotate: '18deg' }],
                ...(Platform.OS === 'web'
                  ? ({ backgroundImage: `linear-gradient(90deg, transparent, ${colors.paper}, transparent)` } as any)
                  : { backgroundColor: colors.paper, opacity: 0.25 }),
              }}
            />
            <View style={{ width: 8, height: 8, alignItems: 'center', justifyContent: 'center' }}>
              <Animated.View style={{ position: 'absolute', width: 8, height: 8, borderRadius: radii.full, backgroundColor: colors.accent, opacity: beatOpacity, transform: [{ scale: beatScale }] }} />
              <View style={{ width: 8, height: 8, borderRadius: radii.full, backgroundColor: colors.accent }} />
            </View>
            <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, letterSpacing: 1.4, fontWeight: fontWeight.semibold, color: colors.accentInk }}>
              COMING SOON
            </Text>
          </View>

          {/* Hero title — sourcing is the main content now */}
          <Text style={{ fontSize: isMobile ? fontSize.xl : fontSize['2xl'], fontWeight: fontWeight.semibold, color: colors.ink, letterSpacing: -0.5, textAlign: 'center' }}>
            Every board, one pipeline.
          </Text>

          {/* Rotating attractive hook */}
          <Animated.Text
            style={{
              marginTop: 6, minHeight: 20, maxWidth: 480,
              fontSize: fontSize.sm, lineHeight: 19, color: colors.ink3, textAlign: 'center',
              opacity: msg, transform: [{ translateY: msgTranslate }],
            }}
          >
            {HOPEFUL_MESSAGES[msgIndex]}
          </Animated.Text>

          {/* The two headline boards. These are the integrations people ask for
              first, so they get the tiles; everything else stays a chip below. */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginTop: 18, width: '100%', maxWidth: 620 }}>
            <View style={{ flexGrow: 1, flexBasis: 260, minWidth: 0 }}>
              <BulkTile
                theme={theme}
                index={0}
                enter={enter}
                float={float}
                icon="users"
                title="LinkedIn Recruiter"
                desc="Search, InMail → sourced candidates"
              />
            </View>
            <View style={{ flexGrow: 1, flexBasis: 260, minWidth: 0 }}>
              <BulkTile
                theme={theme}
                index={1}
                enter={enter}
                float={float}
                icon="layers"
                title="Naukri.com"
                desc="Post roles → applicants, auto-matched"
              />
            </View>
          </View>

          {/* Shimmer divider */}
          <View style={{ width: '100%', maxWidth: 440, height: 1, marginTop: 16, marginBottom: 14, backgroundColor: colors.line, overflow: 'hidden' }}>
            <Animated.View
              style={{
                position: 'absolute', top: 0, bottom: 0, width: 120,
                transform: [{ translateX: shimmer.interpolate({ inputRange: [0, 1], outputRange: [-120, 440] }) }],
                ...(Platform.OS === 'web'
                  ? ({ backgroundImage: `linear-gradient(90deg, transparent, ${colors.accent}, transparent)` } as any)
                  : { backgroundColor: colors.accent }),
              }}
            />
          </View>

          {/* ── Coming soon — the rest of the hiring stack (compact footer) ── */}
          <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', gap: 10 }}>
            {/* Coming soon pill with a light sweep */}
            <View
              style={{
                flexDirection: 'row', alignItems: 'center', gap: 7,
                paddingHorizontal: 12, paddingVertical: 5, borderRadius: radii.full,
                borderWidth: 1, borderColor: colors.accentLine, backgroundColor: colors.accentSoft,
                overflow: 'hidden',
              }}
            >
              <Animated.View
                pointerEvents="none"
                style={{
                  position: 'absolute', top: 0, bottom: 0, width: 60, opacity: 0.5,
                  transform: [{ translateX: sweepX }, { rotate: '18deg' }],
                  ...(Platform.OS === 'web'
                    ? ({ backgroundImage: `linear-gradient(90deg, transparent, ${colors.paper}, transparent)` } as any)
                    : { backgroundColor: colors.paper, opacity: 0.25 }),
                }}
              />
              <View style={{ width: 7, height: 7, borderRadius: radii.full, backgroundColor: colors.accent }} />
              <Text style={{ fontFamily: font.mono, fontSize: 10, letterSpacing: 1.3, fontWeight: fontWeight.semibold, color: colors.accentInk }}>
                COMING SOON
              </Text>
            </View>
            <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontWeight: fontWeight.medium }}>
              Indeed, Instahyre, CSV feeds and inbound webhooks — on the way.
            </Text>
          </View>

          {/* One quiet line, deliberately not a hero: bulk import shipped and lives
              on Jobs / Candidates, and someone landing here looking for it should
              be pointed there rather than shown a "coming soon" for something
              that already works. */}
          <Text style={{ fontSize: fontSize.xs, color: colors.ink4, textAlign: 'center', marginTop: 10 }}>
            Importing files already works — use Bulk import on Jobs or Candidates.
          </Text>

          {/* Preserved data — what's arriving (portals + SK Bridge), compact */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 12 }}>
            {arriving.map((item, i) => (
              <ArrivingChip key={item.key} index={i} enter={enter} label={item.label} hint={item.hint} theme={theme} />
            ))}
          </View>
        </View>
      </Animated.View>
    </ScrollView>
  );
}

/** A premium preview tile for a bulk-import type — coming soon (non-interactive). */
function BulkTile({
  theme, index, enter, float, icon, title, desc,
}: {
  theme: ReturnType<typeof useTheme>['theme'];
  index: number;
  enter: Animated.Value;
  float: Animated.Value;
  icon: React.ComponentProps<typeof Icon>['name'];
  title: string;
  desc: string;
}) {
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const local = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const t = Animated.timing(local, {
      toValue: 1, duration: 520, delay: 320 + index * 120,
      easing: Easing.out(Easing.cubic), useNativeDriver: NATIVE,
    });
    t.start();
    return () => t.stop();
  }, [local, index]);

  // The icon halo bobs gently, out of phase per tile, for a lively feel.
  const bob = float.interpolate({ inputRange: [0, 1], outputRange: [0, index % 2 === 0 ? -4 : -6] });

  return (
    <Animated.View
      style={{
        opacity: local,
        transform: [{ translateY: local.interpolate({ inputRange: [0, 1], outputRange: [16, 0] }) }],
        flexDirection: 'row', alignItems: 'center', gap: 14,
        paddingVertical: 16, paddingHorizontal: 16,
        borderRadius: radii.lg, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper2,
        overflow: 'hidden',
        ...(Platform.OS === 'web'
          ? ({
              backgroundImage: `linear-gradient(150deg, ${colors.accentSoft} 0%, ${colors.paper2} 55%)`,
              boxShadow: `0 12px 30px -22px ${colors.accent}88`,
            } as any)
          : {}),
      }}
    >
      <Animated.View
        style={{
          width: 46, height: 46, borderRadius: radii.full,
          alignItems: 'center', justifyContent: 'center',
          backgroundColor: colors.accent, transform: [{ translateY: bob }],
          ...(Platform.OS === 'web'
            ? ({ backgroundImage: `linear-gradient(160deg, ${colors.accent}, ${colors.accentInk})`, boxShadow: `0 10px 22px -12px ${colors.accent}` } as any)
            : {}),
        }}
      >
        <Icon name={icon} size={20} color={colors.onAccent} />
      </Animated.View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink }}>{title}</Text>
        <Text style={{ fontSize: fontSize.xs, color: colors.ink3, marginTop: 2 }} numberOfLines={1}>{desc}</Text>
      </View>
      {/* Coming-soon badge (replaces the upload action) */}
      <View
        style={{
          paddingHorizontal: 10, paddingVertical: 5, borderRadius: radii.full,
          borderWidth: 1, borderColor: colors.accentLine, backgroundColor: colors.accentSoft,
        }}
      >
        <Text style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: 1, fontWeight: fontWeight.semibold, color: colors.accentInk }}>
          SOON
        </Text>
      </View>
    </Animated.View>
  );
}

function ArrivingChip({
  index, enter, label, hint, theme,
}: {
  index: number;
  enter: Animated.Value;
  label: string;
  hint: string;
  theme: ReturnType<typeof useTheme>['theme'];
}) {
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const local = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const t = Animated.timing(local, {
      toValue: 1,
      duration: 460,
      delay: 220 + index * 55,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: NATIVE,
    });
    t.start();
    return () => t.stop();
  }, [local, index]);

  return (
    <Animated.View
      style={{
        opacity: local,
        transform: [{ translateY: local.interpolate({ inputRange: [0, 1], outputRange: [10, 0] }) }],
        flexDirection: 'row', alignItems: 'center', gap: 7,
        paddingHorizontal: 12, paddingVertical: 7, borderRadius: radii.full,
        borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper2,
      }}
    >
      <View style={{ width: 6, height: 6, borderRadius: radii.full, backgroundColor: colors.accent }} />
      <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink2, fontWeight: fontWeight.medium }} numberOfLines={1}>
        {label}
      </Text>
      <Text style={{ fontSize: 10, color: colors.ink4 }} numberOfLines={1}>· {hint}</Text>
    </Animated.View>
  );
}
